// Third-party
import React from 'react';
import classNames from 'classnames';

// Components
import { InfoTooltip, Tooltip } from '@/views/components/tooltips';
import { HoldingIcon } from '@/views/components/common';
import { Typography } from '@capintel/component-lib/typography';

// Utils
import { TEMPERATURE_RANKING, ESGColourDefinition, ImpliedTemperatureLimit } from '@/enums/Esg';
import { FormattedMessage, formatMessage, formatLocalizedString } from '@/utils/i18n';
import ComparisonItem from '@/types/comparison-item';
import ThemeMode from '@/types/theme-mode';
import { ALLOWED_HTML_TAGS } from '@/views/components/i18n/FormattedMessageV2';

type Props = {
  selectedItems: Array<ComparisonItem>;
  theme?: ThemeMode;
};

const ImpliedTemperatureRise = ({ selectedItems, theme }: Props) => {
  const unitScale = 'C';
  const decimalsAllowed = 1;
  const [firstItem, secondItem]: Array<ComparisonItem> = selectedItems || [];
  const hasComparedData = selectedItems.length > 1;
  const firstTemperature = firstItem?.esg?.impliedTemperatureRise;

  const getTemperatureRanking = (temperature?: number) => {
    if (!temperature) {
      return TEMPERATURE_RANKING.UNAVAILABLE;
    }

    if (temperature <= ImpliedTemperatureLimit.ALIGNED) {
      return TEMPERATURE_RANKING.ALIGNED;
    }

    if (temperature <= ImpliedTemperatureLimit.MISALIGNED) {
      return TEMPERATURE_RANKING.MISALIGNED;
    }

    return TEMPERATURE_RANKING.STRONGLY_MISALIGNED;
  };

  const getTemperatureColor = (temperature?: number) => {
    if (!temperature) return theme === ThemeMode.LIGHT ? ESGColourDefinition.UNAVAILABLE_THEME_LIGHT : ESGColourDefinition.UNAVAILABLE;
    if (temperature <= ImpliedTemperatureLimit.ALIGNED_LOW) return ESGColourDefinition.LOW;
    if (temperature <= ImpliedTemperatureLimit.ALIGNED) return ESGColourDefinition.MODERATE;
    if (temperature <= ImpliedTemperatureLimit.MISALIGNED) return ESGColourDefinition.LOW_MODERATE;
    return ESGColourDefinition.VERY_LOW_MODERATE;
  };

  const getDeltaColor = (temperature: number) => {
    if (!firstTemperature || !temperature) return ESGColourDefinition.UNAVAILABLE;
    if (firstTemperature && temperature && firstTemperature === temperature) return ESGColourDefinition.MODERATE;
    if (firstTemperature > temperature) return ESGColourDefinition.TEXT_ALIGNED;
    return ESGColourDefinition.TEXT_STRONGLY_MISALIGNED;
  };

  const renderDelta = (temperature: number) => {
    if (!firstTemperature) return;

    const delta = +(temperature - firstTemperature).toFixed(decimalsAllowed);
    const temperatureColor = getDeltaColor(temperature);

    return (
      <span className="delta" style={{ color: temperatureColor }}>
        <i className={classNames('icon', delta !== 0 ? `icon-arrow-${delta < 0 ? 'down' : 'up'}` : 'icon-minus-med')} />
        {Math.abs(delta)}°{unitScale}
      </span>
    );
  };

  const renderTemperatureData = (item?: ComparisonItem, isCompared?: boolean) => {
    const temperatureRanking = getTemperatureRanking(item?.esg?.impliedTemperatureRise);
    const temperatureColor = getTemperatureColor(item?.esg?.impliedTemperatureRise);

    return (
      <div className={classNames(`temperature-ranking ${temperatureRanking?.toLowerCase()}`)}>
        <span className="temperature">
          {hasComparedData && item && (
            <Tooltip message={formatLocalizedString(item?.name)} data-private data-dd-privacy="mask">
              <span className="custom-tooltip-icon">
                <HoldingIcon type={item?.type} color={item?.color} />
              </span>
            </Tooltip>
          )}
          {!item?.esg?.impliedTemperatureRise ? (
            <span className="not-applicable">
              <FormattedMessage id="Common.NotApplicable" />
            </span>
          ) : (
            <>
              <span className="value">{item?.esg?.impliedTemperatureRise.toFixed(decimalsAllowed)}</span>
              <span className="indicator">°{unitScale}</span>
            </>
          )}

          {isCompared && firstTemperature && item?.esg?.impliedTemperatureRise && renderDelta(item?.esg?.impliedTemperatureRise)}
        </span>
        <div className="label" style={{ borderColor: temperatureColor }}>
          {formatMessage(`ESGComparisonTool.ImpliedTemperatureRise.TemperatureRanking.${temperatureRanking}`)}
        </div>
      </div>
    );
  };

  return (
    <div className="implied-temperature-rise">
      <div className="implied-temperature-rise-title">
        <Typography as={ALLOWED_HTML_TAGS.H2} variant="header2Xs">
          <FormattedMessage id="ESGComparisonTool.ImpliedTemperatureRise.Title" />
          {hasComparedData && (
            <InfoTooltip
              key="ESGComparisonTool.ImpliedTemperatureRise.Tooltip"
              message={formatMessage('ESGComparisonTool.ImpliedTemperatureRise.EducationalText')}
              data-private
              data-dd-privacy="mask"
            />
          )}
        </Typography>
      </div>

      <div className="implied-temperature-rise-info d-flex flex-row align-items-center">
        {renderTemperatureData(firstItem)}
        {hasComparedData && renderTemperatureData(secondItem, true)}
        {!hasComparedData && <FormattedMessage tagName="p" id="ESGComparisonTool.ImpliedTemperatureRise.EducationalText" />}
      </div>
    </div>
  );
};

export default ImpliedTemperatureRise;
